<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Report Cards
            </h1>
            <button @click="openGenerateModal" class="btn btn-primary">
                <Plus class="w-4 h-4 mr-1" />
                Generate Report Cards
            </button>
        </div>

        <!-- Search and Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="space-y-3">
                <input
                    v-model="searchQuery"
                    @input="debouncedSearch"
                    type="text"
                    placeholder="Search by student name..."
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />

                <div class="flex flex-wrap gap-3">
                    <!-- Class Filter -->
                    <SearchableSelect
                        v-model="filters.class_id"
                        :options="classOptions"
                        placeholder="All Classes"
                        :option-label="getClassLabel"
                        option-value="id"
                        class="flex-1 min-w-[160px]"
                        @update:model-value="fetchReportCards"
                    />

                    <!-- Semester Filter -->
                    <SearchableSelect
                        v-model="filters.semester_id"
                        :options="semesterOptions"
                        placeholder="All Semesters"
                        :option-label="getSemesterLabel"
                        option-value="id"
                        class="flex-1 min-w-[160px]"
                        @update:model-value="fetchReportCards"
                    />

                    <!-- Status Filter -->
                    <SearchableSelect
                        v-model="filters.is_published"
                        :options="statusOptions"
                        placeholder="All Statuses"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchReportCards"
                    />

                    <button
                        @click="resetFilters"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <RotateCcw class="w-4 h-4" />
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Weights Settings (admin only) -->
        <div
            v-if="isAdmin"
            class="bg-white dark:bg-gray-800 rounded-lg shadow p-4"
        >
            <div class="flex flex-wrap items-center gap-4">
                <div class="mr-auto">
                    <h2
                        class="text-sm font-semibold text-gray-900 dark:text-white"
                    >
                        Score Weights
                    </h2>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Assignment {{ settings.assignment_weight }}% · Exam
                        {{ settings.exam_weight }}% · Attendance
                        {{ settings.attendance_weight }}%
                    </p>
                </div>
                <button
                    @click="openWeightsModal"
                    class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                >
                    <Settings2 class="w-4 h-4" />
                    Configure
                </button>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="6" :cols="8" />
        </div>

        <!-- Error State -->
        <div
            v-if="error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ error }}
                    </p>
                    <button
                        @click="clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Data Table -->
        <div
            v-if="!loading && !error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Student
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Class
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Semester
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Assignment
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Exam
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Attendance
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Final
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Rank
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Status
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="card in reportCards"
                            :key="card.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white"
                            >
                                {{ card.student?.user?.name || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ card.class?.name || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ card.semester?.name || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ card.assignment_average ?? "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ card.exam_average ?? "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{
                                    card.attendance_percentage !== null &&
                                    card.attendance_percentage !== undefined
                                        ? card.attendance_percentage + "%"
                                        : "-"
                                }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white"
                            >
                                {{
                                    card.final_score !== null &&
                                    card.final_score !== undefined
                                        ? `${card.final_score} (${card.grade})`
                                        : "-"
                                }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{
                                    card.rank_in_class
                                        ? `${card.rank_in_class}/${card.total_students}`
                                        : "-"
                                }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                    :class="
                                        card.is_published
                                            ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                                            : 'bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-200'
                                    "
                                >
                                    {{
                                        card.is_published
                                            ? "Published"
                                            : "Draft"
                                    }}
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                            >
                                <button
                                    @click="viewCard(card)"
                                    title="View Report Card"
                                    class="inline-flex items-center justify-center p-2 text-success-600 dark:text-success-400 hover:text-success-900 dark:hover:text-success-300 rounded-md hover:bg-success-50 dark:hover:bg-success-900/20 transition-colors mr-2"
                                >
                                    <Eye class="w-4 h-4" />
                                </button>
                                <button
                                    @click="downloadPdf(card)"
                                    title="Download PDF"
                                    class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                                >
                                    <Download class="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <EmptyState
                v-if="!loading && reportCards.length === 0"
                icon="FileTextIcon"
                title="No report cards found"
                description="Generate report cards for a class and semester to get started."
            />

            <BasePagination
                v-if="reportCards.length > 0"
                :pagination="meta"
                :per-page="filters.per_page || 15"
                :per-page-options="[10, 15, 25, 50, 100]"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </div>

        <!-- Generate Modal -->
        <Teleport to="body">
            <div
                v-if="showGenerateModal"
                class="fixed inset-0 z-50 overflow-y-auto"
            >
                <div
                    class="fixed inset-0 bg-black bg-opacity-50"
                    @click="showGenerateModal = false"
                ></div>
                <div
                    class="relative min-h-full flex items-center justify-center p-4"
                >
                    <div
                        class="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 space-y-4"
                    >
                        <h3
                            class="text-lg font-semibold text-gray-900 dark:text-white"
                        >
                            Generate Report Cards
                        </h3>
                        <SearchableSelect
                            v-model="generateForm.class_id"
                            :options="classOptions"
                            placeholder="Select Class"
                            :option-label="getClassLabel"
                            option-value="id"
                        />
                        <SearchableSelect
                            v-model="generateForm.semester_id"
                            :options="semesterOptions"
                            placeholder="Select Semester"
                            :option-label="getSemesterLabel"
                            option-value="id"
                        />
                        <div class="flex justify-end gap-3 pt-2">
                            <button
                                @click="showGenerateModal = false"
                                class="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                            >
                                Cancel
                            </button>
                            <button
                                @click="handleGenerate"
                                :disabled="
                                    generating ||
                                    !generateForm.class_id ||
                                    !generateForm.semester_id
                                "
                                class="btn btn-primary disabled:opacity-50"
                            >
                                {{ generating ? "Generating..." : "Generate" }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Teleport>

        <!-- Weights Modal -->
        <Teleport to="body">
            <div
                v-if="showWeightsModal"
                class="fixed inset-0 z-50 overflow-y-auto"
            >
                <div
                    class="fixed inset-0 bg-black bg-opacity-50"
                    @click="showWeightsModal = false"
                ></div>
                <div
                    class="relative min-h-full flex items-center justify-center p-4"
                >
                    <div
                        class="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 space-y-4"
                    >
                        <h3
                            class="text-lg font-semibold text-gray-900 dark:text-white"
                        >
                            Score Weights
                        </h3>
                        <div>
                            <label
                                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                Assignment Weight (%)
                            </label>
                            <input
                                v-model.number="weightsForm.assignment_weight"
                                type="number"
                                min="0"
                                max="100"
                                class="form-input w-full"
                            />
                        </div>
                        <div>
                            <label
                                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                Exam Weight (%)
                            </label>
                            <input
                                v-model.number="weightsForm.exam_weight"
                                type="number"
                                min="0"
                                max="100"
                                class="form-input w-full"
                            />
                        </div>
                        <div>
                            <label
                                class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            >
                                Attendance Weight (%)
                            </label>
                            <input
                                v-model.number="weightsForm.attendance_weight"
                                type="number"
                                min="0"
                                max="100"
                                class="form-input w-full"
                            />
                        </div>
                        <p
                            class="text-sm"
                            :class="
                                weightsSum === 100
                                    ? 'text-success-600 dark:text-success-400'
                                    : 'text-danger-600 dark:text-danger-400'
                            "
                        >
                            Total: {{ weightsSum }}% (must be exactly 100%)
                        </p>
                        <div class="flex justify-end gap-3 pt-2">
                            <button
                                @click="showWeightsModal = false"
                                class="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                            >
                                Cancel
                            </button>
                            <button
                                @click="handleSaveWeights"
                                :disabled="savingWeights || weightsSum !== 100"
                                class="btn btn-primary disabled:opacity-50"
                            >
                                {{ savingWeights ? "Saving..." : "Save" }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { debounce } from "lodash-es";
import SearchableSelect from "@/components/SearchableSelect.vue";
import BasePagination from "@/components/BasePagination.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import { RotateCcw, Eye, Download, Plus, Settings2 } from "@lucide/vue";
import reportCardApi, { ReportCard } from "@/api/reportCard";
import { useAuthStore } from "@/stores/authStore";

const router = useRouter();
const authStore = useAuthStore();

const reportCards = ref<ReportCard[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const meta = ref<any>(null);

const searchQuery = ref("");
const filters = ref<Record<string, any>>({
    class_id: null,
    semester_id: null,
    is_published: null,
    per_page: 15,
});

const classOptions = ref<any[]>([]);
const semesterOptions = ref<any[]>([]);

const statusOptions = [
    { label: "Published", value: true },
    { label: "Draft", value: false },
];

const settings = ref<Record<string, any>>({
    assignment_weight: 40,
    exam_weight: 60,
    attendance_weight: 0,
});

const showGenerateModal = ref(false);
const generating = ref(false);
const generateForm = ref({ class_id: "", semester_id: "" });

const showWeightsModal = ref(false);
const savingWeights = ref(false);
const weightsForm = ref({
    assignment_weight: 40,
    exam_weight: 60,
    attendance_weight: 0,
});

const isAdmin = computed(() =>
    authStore.hasAnyRole(["administrator", "super-administrator"]),
);

const weightsSum = computed(() => {
    const { assignment_weight, exam_weight, attendance_weight } =
        weightsForm.value;
    return (
        (Number(assignment_weight) || 0) +
        (Number(exam_weight) || 0) +
        (Number(attendance_weight) || 0)
    );
});

const hasActiveFilters = computed(() => {
    const { class_id, semester_id, is_published } = filters.value;
    return (
        searchQuery.value.trim() !== "" ||
        (class_id !== null && class_id !== undefined) ||
        (semester_id !== null && semester_id !== undefined) ||
        (is_published !== null && is_published !== undefined)
    );
});

const debouncedSearch = debounce(() => {
    fetchReportCards();
}, 500);

async function fetchReportCards() {
    loading.value = true;
    error.value = null;

    try {
        const response = await reportCardApi.getAll({
            ...filters.value,
            search: searchQuery.value || undefined,
        });

        reportCards.value = response.data.items;
        meta.value = response.data.meta;
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to fetch report cards";
    } finally {
        loading.value = false;
    }
}

async function fetchSettings() {
    try {
        settings.value = await reportCardApi.getSettings();
    } catch {
        // Settings are optional for non-admin users.
    }
}

function resetFilters() {
    filters.value = {
        class_id: null,
        semester_id: null,
        is_published: null,
        per_page: 15,
    };
    searchQuery.value = "";
    fetchReportCards();
}

function clearError() {
    error.value = null;
}

async function fetchOptions() {
    const options = await reportCardApi.getOptions();

    classOptions.value = options.data.classes || [];
    semesterOptions.value = options.data.semesters || [];
}

function getClassLabel(option: any) {
    return `${option.grade_level || ""} - ${option.name}`;
}

function getSemesterLabel(option: any) {
    return `${option.academic_year?.name || ""} - ${option.name}`;
}

function openGenerateModal() {
    generateForm.value = { class_id: "", semester_id: "" };
    showGenerateModal.value = true;
}

function openWeightsModal() {
    weightsForm.value = {
        assignment_weight: Number(settings.value.assignment_weight),
        exam_weight: Number(settings.value.exam_weight),
        attendance_weight: Number(settings.value.attendance_weight),
    };
    showWeightsModal.value = true;
}

async function handleGenerate() {
    generating.value = true;
    error.value = null;

    try {
        await reportCardApi.generate(
            generateForm.value.class_id,
            generateForm.value.semester_id,
        );
        showGenerateModal.value = false;
        filters.value.class_id = generateForm.value.class_id;
        filters.value.semester_id = generateForm.value.semester_id;
        filters.value.page = 1;
        await fetchReportCards();
    } catch (err: any) {
        error.value =
            err.response?.data?.message || "Failed to generate report cards";
    } finally {
        generating.value = false;
    }
}

async function handleSaveWeights() {
    savingWeights.value = true;

    try {
        settings.value = await reportCardApi.updateSettings({
            assignment_weight: weightsForm.value.assignment_weight,
            exam_weight: weightsForm.value.exam_weight,
            attendance_weight: weightsForm.value.attendance_weight,
        });
        showWeightsModal.value = false;
    } catch (err: any) {
        error.value = err.response?.data?.message || "Failed to save weights";
    } finally {
        savingWeights.value = false;
    }
}

function viewCard(card: ReportCard) {
    router.push(`/reports/${card.id}`);
}

function downloadPdf(card: ReportCard) {
    window.open(reportCardApi.getPdfStreamUrl(card.id), "_blank");
}

function goToPage(page: number) {
    if (meta.value) {
        filters.value.page = page;
        fetchReportCards();
    }
}

function handlePerPageChange(value: number) {
    filters.value.per_page = value;
    filters.value.page = 1;
    fetchReportCards();
}

onMounted(() => {
    fetchReportCards();
    fetchOptions();
    fetchSettings();
});
</script>
